import { CustomHeaderProps } from 'ag-grid-react';
import { DisplayedPrice } from 'apiConnector/smart-quote/model';
import { ConfirmModal } from 'components/shared/Modals/ConfirmModal';
import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useSolveModeTranslation } from 'hooks/use-solve-mode';
import { useToggle } from 'hooks/useToggle';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import useSaveDisplayedPrices from 'routes/smart-quote/hooks/use-save-displayed-prices';
import { SmartQuoteParsingResult } from 'state/workspaces/smart-quote/model';

import './style.scss';

type PriceColumnHeaderProps = CustomHeaderProps<SmartQuoteParsingResult> & {
  solveMode: string;
};

const PriceColumnHeader = ({ solveMode, api }: PriceColumnHeaderProps) => {
  const { formatMessage } = useIntl();
  const { isOn: isModalOpened, toggleOff: closeModal, toggleOn: openModal } = useToggle();

  const tabId = useAssertedActiveTabId();
  const translateSolveMode = useSolveModeTranslation();
  const saveDisplayedPrices = useSaveDisplayedPrices();

  const [results, setResults] = useState<SmartQuoteParsingResult[]>([]);

  useEffect(() => {
    const updateHeaderState = () => {
      const results: SmartQuoteParsingResult[] = [];

      api.forEachNode(node => {
        if (node.data && node.data.solveMode === solveMode) {
          results.push(node.data);
        }
      });

      setResults(results);
    };

    api.addEventListener('rowDataUpdated', updateHeaderState);

    updateHeaderState();

    return () => {
      api.removeEventListener('rowDataUpdated', updateHeaderState);
    };
  }, [api, solveMode]);

  const hasPricesToDisplay = results.some(result => result.quoteStatus === 'Quoted');

  const displayedPrices: DisplayedPrice[] = results
    .filter(result => result.isDisplayedPrice !== undefined)
    .map(result => ({
      resultId: parseInt(result.id),
      isDisplayedPrice: result.isDisplayedPrice!,
    }));

  const hasAllPricesDisplayed = displayedPrices.every(displayedPrice => displayedPrice.isDisplayedPrice);

  const handleConfirm = () => {
    closeModal();
    showAllPrices();
  };

  const showAllPrices = () => {
    const newDisplayedPrices = displayedPrices.map(price => ({
      ...price,
      isDisplayedPrice: !hasAllPricesDisplayed,
    }));

    const promptId = results[0]?.promptId;

    saveDisplayedPrices({
      tabId,
      promptId,
      displayedPrices: newDisplayedPrices,
    });
  };

  const translatedSolveMode = translateSolveMode(solveMode);

  return (
    <div className="d-flex align-items-center justify-content-between gap-2 w-100">
      <span className="text-truncate" title={translatedSolveMode}>
        {translatedSolveMode}
      </span>
      {hasPricesToDisplay && (
        <>
          <button
            type="button"
            className="btn btn-icon btn-flat-secondary"
            onClick={hasAllPricesDisplayed ? handleConfirm : openModal}
            title={formatMessage({ id: 'smartQuote.workspace.main.priceColumnHeader.viewAllPricesBtnTitle' })}
          >
            <i className={`icon ${!hasAllPricesDisplayed ? 'icon-outlined' : ''}`}>
              {hasAllPricesDisplayed ? 'visibility' : 'visibility_off'}
            </i>
          </button>
          {!hasAllPricesDisplayed && (
            <ConfirmModal
              titleKey="smartQuote.workspace.main.priceColumnHeader.modal.title"
              confirmBtnKey="confirm"
              dialogClassName="price-column-header-modal"
              titleClassName="me-auto text-warning"
              confirmBtnClassName="btn btn-lg btn-warning"
              cancelBtnClassName="btn btn-lg btn-flat-secondary"
              show={isModalOpened}
              onClosed={closeModal}
              onConfirm={handleConfirm}
              withCloseButton
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: formatMessage({ id: 'smartQuote.workspace.main.priceColumnHeader.modal.description' }),
                }}
              />
            </ConfirmModal>
          )}
        </>
      )}
    </div>
  );
};

export default PriceColumnHeader;
